// Push-notification opt-in + environment detection helpers.
import { getMessaging, getToken } from "firebase/messaging";
import { doc, updateDoc } from "firebase/firestore";
import { app, db, VAPID_KEY } from "./firebase";

/**
 * Request notification permission, fetch an FCM token, and persist it to the
 * guest doc. Returns true if secure comms are now enabled. Guards against
 * unsupported browsers and a missing VAPID key.
 */
export async function requestPush(uid: string): Promise<boolean> {
  try {
    if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) return false;
    if (!VAPID_KEY) return false;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return false;

    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return false;

    await updateDoc(doc(db, "guests", uid), { pushToken: token, pushEnabled: true });
    return true;
  } catch {
    return false;
  }
}

/** True when running as an installed / home-screen PWA. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function isIOS(): boolean {
  const ua = navigator.userAgent || "";
  const iOSLike = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; detect via touch.
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSLike || iPadOS;
}

/** Detect common in-app browsers that block camera/GPS. */
export function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Line|WhatsApp|Messenger/i.test(ua);
}
